import config from "appConfig";

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>In this chapter, we will focus on transducer modelling. We will start with electromechanical transducers such as the shaker and the geophone. Then, we will move on to electro-acoustic transducers such as the microphone and the loudspeaker.<br class=newline>This Lesson is dedicated to the shaker, which is a test device used to excite mechanical structures, either for endurance assessment or modal analysis. It also has applications in the field of home cinema where it is used to generate vibrations in seats. It is therefore an electromechanical source. This Lesson is devoted to the modelling of this type of transducer [3, 1, 2].
<h3 class=sectionHead>Working principle</h3>
<p>
   A schematic diagram of a moving coil shaker is represented in <a href="#" data-img-alt="Figure 1. Moving coil shaker (cut view)." data-img-src=${config.ASSETS_URL}/04/12/theory/figure-1.svg>Figure 1</a>. A cylindrical coil is attached to the table, which is connected to the body by a suspension allowing movement along the vertical axis. A magnet and the pole pieces are rigidly attached to the body creating a radial magnetic field
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>B</mi>
   </math>
   in the coil region. When the coil is supplied with an electrical voltage, a current flows through it. A vertical Lorentz force is then created, putting the table into vibration. As the coil moves perpendicular to the magnetic field, an induced EMF is created at its terminals which partially opposes the driving voltage. The object under test, rigidly fixed to the table, is therefore set into vibration when the shaker is powered.
   <figure class=figure>
<p><img alt="Figure 1. Moving coil shaker (cut view)." src=${config.ASSETS_URL}/04/12/theory/figure-1.svg><figcaption class=caption>Figure 1. Moving coil shaker (cut view).</figcaption></figure>
<h3 class=sectionHead>Behavioural equation</h3>
<p>Let us assume that at low frequency, the electro-mechanical behaviour of the shaker can be modelled by using the following assumptions:
<ul class=itemize1>
   <li class=itemize>
      the table-suspension-coil assembly can be considered as a damped mechanical oscillator with mass
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
      </math>, compliance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
      </math>
      and resistance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
      </math>
      vibrating with a velocity
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      the coil has a length
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>ℓ</mi>
      </math>
      and is located in a zone where the magnetic field
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>B</mi>
      </math>
      is assumed to be radial and constant,
   <li class=itemize>
      the driving voltage is denoted
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>U</mi>
      </math>
      while the current flowing in the coil of impedance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mi mathvariant=italic>jω</mi>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
      </math>
      is denoted
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>I</mi>
      </math>,
   <li class=itemize>
      the rubber feet dampen vibrations and impose a null velocity of the body of mass
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>.
</ul>
<p>
   The mechanical network representing the shaker behaviour is shown in <a href="#" data-img-alt="Figure 2. Mechanical system representing the shaker behaviour." data-img-src=${config.ASSETS_URL}/04/12/theory/figure-2.svg>Figure 2</a>, it corresponds to a 1 Degree Of Freedom (1-DOF) system.
   <figure class=figure>
<p><img alt="Figure 2. Mechanical system representing the shaker behaviour." src=${config.ASSETS_URL}/04/12/theory/figure-2.svg><figcaption class=caption>Figure 2. Mechanical system representing the shaker behaviour.</figcaption></figure>
<p>
   Now, let us write the equations governing the shaker behaviour, starting with the second law of Newton applied to the table-suspension-coil assembly for a harmonic motion of pulsation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>BℓI</mi>
   </math>
   is the Lorentz force. It should be noted that the restoring and friction forces of the suspension are opposed to the Lorentz force which is at the origin of the movement. Then, Eq. (1) can be rewritten as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   is the mechanical impedance of the table-suspension-coil assembly. Note that Eq. (2) has the same mathematical form as that of an RLC circuit stimulated by a voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
   </math>. The electrical equation describing the coil behaviour is given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>U</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mi>I</mi>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>e</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>Bℓ</mi>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   is the EMF caused by the coil vibration. Using the electromechanical analogies and a gyrator for the electrodynamic coupling, the behavioural equations can be expressed by the circuit depicted in <a href="#" data-img-alt="Figure 3. Equivalent electrical circuit representing the shaker behaviour." data-img-src=${config.ASSETS_URL}/04/12/theory/figure-3.svg>Figure 3</a>, each equation corresponding to a closed loop.
   <figure class=figure>
<p><img alt="Figure 3. Equivalent electrical circuit representing the shaker behaviour." src=${config.ASSETS_URL}/04/12/theory/figure-3.svg><figcaption class=caption>Figure 3. Equivalent electrical circuit representing the shaker behaviour.</figcaption></figure>
<p>Finally, the two behavioural equations can be put into the form of the following coupled system
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable align=axis equalcolumns=false equalrows=false style="" class=array>
                     <mtr>
                        <mtd columnalign=center class=array>
                           <mi>U</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mi>I</mi>
                           <mo class=MathClass-bin>+</mo>
                           <mi mathvariant=italic>Bℓ</mi>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd columnalign=center class=array>
                           <mi mathvariant=italic>BℓI</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(4)
</table>
<p>The resolution of the electrical circuit in <a href="#" data-img-alt="Figure 3. Equivalent electrical circuit representing the shaker behaviour." data-img-src=${config.ASSETS_URL}/04/12/theory/figure-3.svg>Figure 3</a> or the system (4) can be conducted equivalently.
<h3 class=sectionHead>Electrical impedance</h3>
<p>
   It is possible to calculate the expression of the shaker electrical impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>U</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>I</mi>
   </math>
   by writing the expression of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   as the function of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
   </math>
   from the second equation of coupled system (4) and use it in the first equation to get
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∕</mo>
            <mi>I</mi>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   The electrical impedances
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   are plotted in <a href="#" data-img-alt="Figure 4. Electrical impedances <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>e</mi></mrow></msub></math> and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>s</mi></mrow></msub></math>." data-img-src=${config.ASSETS_URL}/04/12/theory/figure-4.svg>Figure 4</a> for a typical shaker whose parameters are given in <a href="#" data-img-alt="Table 1. Typical electromechanical parameters of a shaker." data-img-src=${config.ASSETS_URL}/04/12/theory/table-1.svg>Table 1</a>. Note that the term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   causes a bump in the impedance curve. This bump is due to the mechanical resonance of the table-suspension-coil assembly which is maximum when
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   is minimum. This resonance which belongs to the mechanical domain is brought back into the electrical domain by the electrodynamic coupling and can be observed on
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>. The value of the shaker resonance frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   is given for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>Im</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   (where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>Im</mi>
      <mo>⁡</mo>
   </math>
   denotes the imaginary part) by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msqrt>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </msqrt>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(6)
</table>
<div class=table>
   <figure class=float>
      <figcaption class=caption>Table 1. Typical electromechanical parameters of a shaker.</figcaption>
      <img alt="Table 1. Typical electromechanical parameters of a shaker." src=${config.ASSETS_URL}/04/12/theory/table-1.svg>
   </figure>
</div>
<p>
   At
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   is maximum and equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mi>B</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mi>ω</mi>
      <mo class=MathClass-rel>≪</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   at low frequencies. At high frequencies, there is an increase in the electrical impedance due to the inductance of the coil.
   <figure class=figure>
<p><img alt="Figure 4. Electrical impedances <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>e</mi></mrow></msub></math> and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>s</mi></mrow></msub></math>." src=${config.ASSETS_URL}/04/12/theory/figure-4.svg><figcaption class=caption>Figure 4. Electrical impedances <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>e</mi></mrow></msub></math> and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Z</mi></mrow><mrow><mi>s</mi></mrow></msub></math>.</figcaption></figure>
<h3 class=sectionHead>Sensitivity</h3>
<p>
   The shaker sensitivity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   is given by the ratio between the table acceleration
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   and the input voltage (unit: m.s
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
          <mrow>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>.V
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
          <mrow>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msup>
   </math>)
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>a</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>U</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>U</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   for a given pulsation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>. It can be calculated from the coupled system (4). From the second equation, we get
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mi mathvariant=italic>Bℓ</mi>
         </mrow>
      </mfrac>
   </math>
   . Then, we inject
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>I</mi>
   </math>
   into the first equation to get
</p>
<math xmlns=http://www.w3.org/1998/Math/MathML display=block>
   <mi>U</mi>
   <mo class=MathClass-bin>∕</mo>
   <msub>
      <mrow>
         <mi>V</mi>
      </mrow>
      <mrow>
         <mi>t</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mfrac>
      <mrow>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
         <msub>
            <mrow>
               <mi>Z</mi>
            </mrow>
            <mrow>
               <mi>e</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <msup>
            <mrow>
               <mi>B</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
         <msup>
            <mrow>
               <mi>ℓ</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
      </mrow>
      <mrow>
         <mi mathvariant=italic>Bℓ</mi>
      </mrow>
   </mfrac>
</math>
<p>Finally, we obtain
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>U</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jωBℓ</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   As the inductance value is generally small, we can assume that in the low frequency region,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>. Then,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   can be rewritten to get the following expression, with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi>B</mi>
      <msup>
         <mrow>
            <mi>ℓ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mover accent=true>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mo accent=true>~</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>Bℓ</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <mfrac>
                     <mrow>
                        <mi>ω</mi>
                        <msub>
                           <mrow>
                              <mi>R</mi>
                           </mrow>
                           <mrow>
                              <mi>g</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>−</mo>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>s</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   which corresponds to a second order high-pass filter. The value of the curve plateau is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <munder class=msub>
         <mrow>
            <mi class=qopname>lim</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>f</mi>
            <mo class=MathClass-rel>→</mo>
            <mo class=MathClass-bin>+</mo>
            <mi>∞</mi>
         </mrow>
      </munder>
      <mo class=MathClass-rel>|</mo>
      <msub>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mo accent=true>~</mo>
            </mover>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>Bℓ</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>, which corresponds to the nominal sensitivity.<br class=newline>Sensitivity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mo accent=true>~</mo>
            </mover>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   are plotted in <a href="#" data-img-alt="Figure 5. Sensitivity of the shaker." data-img-src=${config.ASSETS_URL}/04/12/theory/figure-5.svg>Figure 5</a>. As expected, we get the shape of a high-pass filter for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>. We also notice that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mo accent=true>~</mo>
            </mover>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   only differ in high frequency, where the inductance causes the response to drop.
   <figure class=figure>
<p><img alt="Figure 5. Sensitivity of the shaker." src=${config.ASSETS_URL}/04/12/theory/figure-5.svg><figcaption class=caption>Figure 5. Sensitivity of the shaker.</figcaption></figure>
<h3 class=sectionHead>Influence of the rubber feet</h3>
<p>
   In practice, the rubber feet may not completely dampen the body vibration, which should then be taken into account. We now have a 2-DOF system (see <a href="#" data-img-alt="Figure 6. Mechanical system representing the shaker behaviour with non rigid feet." data-img-src=${config.ASSETS_URL}/04/12/theory/figure-6.svg>Figure 6</a>) that can be modelled by a system of three equations: two mechanical equations describing the behaviour of the two masses
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   and an electrical equation describing the behaviour of the coil
<table class=equation>
   <tr>
      <td style="font-size: 14px;">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable columnalign="left" align=axis style="" equalcolumns=false equalrows=false columnlines=none class=array>
                     <mtr columnalign="left">
                        <mtd columnalign="left" class=array>
                           <mi>U</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mi>I</mi>
                           <mo class=MathClass-bin>+</mo>
                           <mi mathvariant=italic>Bℓ</mi>
                           <mo class=MathClass-open>(</mo>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>−</mo>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>b</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-close>)</mo>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd columnalign=left class=array>
                           <mi mathvariant=italic>BℓI</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>−</mo>
                           <mrow>
                              <mo fence=true form=prefix>(</mo>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>t</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mfrac>
                                    <mrow>
                                       <mn>1</mn>
                                    </mrow>
                                    <mrow>
                                       <mi mathvariant=italic>jω</mi>
                                       <msub>
                                          <mrow>
                                             <mi>C</mi>
                                          </mrow>
                                          <mrow>
                                             <mi>t</mi>
                                          </mrow>
                                       </msub>
                                    </mrow>
                                 </mfrac>
                              </mrow>
                              <mo fence=true form=postfix>)</mo>
                           </mrow>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>b</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd columnalign=left class=array>
                           <mo class=MathClass-bin>−</mo>
                           <mi mathvariant=italic>BℓI</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>b</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>b</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <mrow>
                              <mo fence=true form=prefix>(</mo>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>t</mi>
                                    </mrow>
                                 </msub>
                                 <mo class=MathClass-bin>+</mo>
                                 <mfrac>
                                    <mrow>
                                       <mn>1</mn>
                                    </mrow>
                                    <mrow>
                                       <mi mathvariant=italic>jω</mi>
                                       <msub>
                                          <mrow>
                                             <mi>C</mi>
                                          </mrow>
                                          <mrow>
                                             <mi>t</mi>
                                          </mrow>
                                       </msub>
                                    </mrow>
                                 </mfrac>
                              </mrow>
                              <mo fence=true form=postfix>)</mo>
                           </mrow>
                           <mo class=MathClass-open>(</mo>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>b</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>−</mo>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-close>)</mo>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mi mathvariant=italic>jω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>, with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   the equivalent compliance and resistance of the rubber feet and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   the velocity of the body.
   <figure class=figure>
<p><img alt="Figure 6. Mechanical system representing the shaker behaviour with non rigid feet." src=${config.ASSETS_URL}/04/12/theory/figure-6.svg><figcaption class=caption>Figure 6. Mechanical system representing the shaker behaviour with non rigid feet.</figcaption></figure>
<p>The following differences can be observed with respect to the case where the body remains immobile
<ul class=itemize1>
   <li class=itemize>the EMF is now proportional to the relative velocity of the coil with respect to the magnet (and therefore the body to which it is attached),
   <li class=itemize>the Lorentz force has different signs in the last two equations by virtue of the action-reaction principle: when it pushes the table upwards it presses the body downwards,
   <li class=itemize>
      the table and the frame are coupled by the suspension, which explains the term
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <mfrac>
            <mrow>
               <mn>1</mn>
            </mrow>
            <mrow>
               <mi mathvariant=italic>jω</mi>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mi>t</mi>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>
      found in the last two equations.
</ul>
<p>
   The behavioural equation of the 2-DOF model can also be expressed by the circuit depicted in <a href="#" data-img-alt="Figure 7. Equivalent electrical circuit representing the shaker behaviour (2-DOF Model)." data-img-src=${config.ASSETS_URL}/04/12/theory/figure-7.svg>Figure 7</a>.
   <figure class=figure>
      <img alt="Figure 7. Equivalent electrical circuit representing the shaker behaviour (2-DOF Model)." src=${config.ASSETS_URL}/04/12/theory/figure-7.svg>
      <figcaption class=caption>Figure 7. Equivalent electrical circuit representing the shaker behaviour (2-DOF Model).</figcaption>
   </figure>
<p>
   From the equation system (10), its is possible to obtain the sensitivity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msubsup>
   </math>
   of the 2-DOF system
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jωBℓ</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msup>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>jω</mi>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi>b</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <msup>
                           <mrow>
                              <mi>B</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <msup>
                           <mrow>
                              <mi>ℓ</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                        <mo class=MathClass-bin>+</mo>
                        <msub>
                           <mrow>
                              <mi>Z</mi>
                           </mrow>
                           <mrow>
                              <mi>e</mi>
                           </mrow>
                        </msub>
                        <mrow>
                           <mo fence=true form=prefix>(</mo>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>R</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>t</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-bin>+</mo>
                              <mfrac>
                                 <mrow>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi mathvariant=italic>jω</mi>
                                    <msub>
                                       <mrow>
                                          <mi>C</mi>
                                       </mrow>
                                       <mrow>
                                          <mi>t</mi>
                                       </mrow>
                                    </msub>
                                 </mrow>
                              </mfrac>
                           </mrow>
                           <mo fence=true form=postfix>)</mo>
                        </mrow>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   If one compares Eqs. (8) and (11), one can see that the coupling between the table and the body introduces the last term of the denominator in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msubsup>
   </math>. The sensitivity calculated with the 2-DOF model is plotted in <a href="#" data-img-alt="Figure 8. Sensitivity of the shaker for two rubber feet damping values." data-img-src=${config.ASSETS_URL}/04/12/theory/figure-8.svg>Figure 8</a> for two different rubber foot damping. It can be seen that when the losses are too small, a resonance appears around
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>30</mn>
   </math> Hz. When the losses are increased, the resonance disappears and the curve corresponding to a motionless body is found.
   <figure class=figure>
<p><img alt="Figure 8. Sensitivity of the shaker for two rubber feet damping values." src=${config.ASSETS_URL}/04/12/theory/figure-8.svg><figcaption class=caption>Figure 8. Sensitivity of the shaker for two rubber feet damping values.</figcaption></figure>
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, an electromechanical shaker was first modelled with a 1-DOF system. It was shown how the coupling equations make it possible to link the mechanical and electrical domains to calculate the electrical impedance and the sensitivity of the shaker. We also saw how to improve the model by adding other behaviours, here the consideration of rubber feet by a 2-DOF model. The model can be further improved to take into account the coupling to the structure to be tested (here the modeling is carried out for the shaker without load) or internal resonances. This improvement can be implemented by adding degrees of freedom and the mechanical elements that characterise them.<br class=newline>
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>

         <dt>1-DOF
         <dd>1 Degree Of Freedom

         <dt>2-DOF
         <dd>2 Degrees Of Freedom

         <dt>EMF
         <dd>ElectroMotive Force
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-martino2018shaker>[1]
   <dd class=thebibliography id=bib-1>
      <p>Jonathan Martino and Kristof Harri. “The shaker parameters estimation, a first step to virtual testing”. In: <span class=cmti-10>MATEC Web of Conferences</span>. Vol. 148. Edp Sciences. 2018, p. 05003.
   <dt class=thebibliography id=X0-martino2019two>[2]
   <dd class=thebibliography id=bib-2>
      <p>Jonathan Martino and Kristof Harri. “Two-port modeling and simulation of an electrodynamic shaker for virtual shaker testing applications”. In: <span class=cmti-10>Journal of Sound and Vibration </span>460 (2019), p. 114835.
   <dt class=thebibliography id=X0-tiwari2017lumped>[3]
   <dd class=thebibliography id=bib-3>
      <p>Nachiketa Tiwari, Amrita Puri, and Abhishek Saraswat. “Lumped parameter modelling and methodology for extraction of model parameters for an electrodynamic shaker”. In: <span class=cmti-10>Journal of Low Frequency Noise, </span><span class=cmti-10>Vibration and Active Control </span>36.2 (2017), pp. 99–115.
</dl>`;

export default theory;
